import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Edit2, Save, X, Check } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

const InterestFormModal = ({ user, setUser, onClose }) => {
  const [editingMode, setEditingMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(() => {
    const savedData = user?.interest_form_data || {};
    return {
      firstName: savedData.firstName || '',
      lastName: savedData.lastName || '',
      email: savedData.email || user?.email || '',
      countryCode: savedData.countryCode || '+91',
      mobile: savedData.mobile || '',
      state: savedData.state || '',
      city: savedData.city || '',
      assetCategory: savedData.assetCategory || 'Luxury Cars',
      brand: savedData.brand || '',
      model: savedData.model || '',
      otherModel: savedData.otherModel || '',
      experiencePreferences: savedData.experiencePreferences || [],
      annualUsage: savedData.annualUsage || '',
      excitementReasons: savedData.excitementReasons || []
    };
  });

  const indianStates = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
    'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
    'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
    'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
    'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Puducherry', 'Chandigarh',
    'Andaman and Nicobar Islands', 'Dadra and Nagar Haveli and Daman and Diu', 'Lakshadweep'
  ];

  const luxuryBrands = {
    'Lamborghini': ['Urus', 'Revuelto', 'Huracán', 'Other'],
    'Ferrari': ['Roma', 'Portofino', 'SF90 Stradale', '812 Superfast', 'Purosangue', 'Other'],
    'Rolls-Royce': ['Phantom', 'Ghost', 'Cullinan', 'Spectre', 'Other'],
    'Bentley': ['Continental GT', 'Flying Spur', 'Bentayga', 'Other'],
    'Porsche': ['911', '718', 'Taycan', 'Panamera', 'Cayenne', 'Macan', 'Other'],
    'Aston Martin': ['DB12', 'Vantage', 'DBX', 'Other'],
    'McLaren': ['720S', 'Artura', 'GT', 'Other'],
    'Mercedes-Benz': ['S-Class', 'E-Class', 'G-Class', 'Maybach S-Class', 'AMG GT', 'GLS', 'GLE', 'Other'],
    'BMW': ['7 Series', '5 Series', 'X7', 'X5', 'M8', 'i7', 'iX', 'Other'],
    'Audi': ['A8', 'A6', 'Q8', 'Q7', 'e-tron GT', 'RS7', 'Other'],
    'Maserati': ['Quattroporte', 'Ghibli', 'Levante', 'Grecale', 'MC20', 'Other'],
    'Jaguar': ['F-Type', 'F-Pace', 'I-Pace', 'Other'],
    'Range Rover': ['Range Rover', 'Range Rover Sport', 'Range Rover Velar', 'Range Rover Evoque', 'Defender', 'Other']
  };

  const experienceOptions = [
    'For occasional road trips',
    'For corporate or business travel',
    'For leisurely weekend drives',
    'For local commute in another city',
    'To experience and enjoy different luxury cars'
  ];

  const excitementOptions = [
    'High Assets: Access to extraordinary cars without full ownership',
    'High Luxury: Becoming a member of the automotive inspired club and enjoy the unique amenities and benefits',
    'High Life: A community of likeminded people and participating in unique events and gatherings'
  ];

  const usageOptions = [
    '1 month', '2 months', '3 months', '4 months', '5 months', '6 months', 'More than 6 months'
  ];

  const handleExperienceToggle = (option) => {
    const current = formData.experiencePreferences;
    if (current.includes(option)) {
      setFormData({
        ...formData,
        experiencePreferences: current.filter(item => item !== option)
      });
    } else {
      setFormData({
        ...formData,
        experiencePreferences: [...current, option]
      });
    }
  };

  const handleExcitementToggle = (option) => {
    const current = formData.excitementReasons;
    if (current.includes(option)) {
      setFormData({
        ...formData,
        excitementReasons: current.filter(item => item !== option)
      });
    } else {
      setFormData({
        ...formData,
        excitementReasons: [...current, option]
      });
    }
  };

  const handleSave = async () => {
    // Validation
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.mobile || !formData.state || !formData.city) {
      toast.error('Please fill all personal information fields');
      return;
    }

    if (!formData.brand || !formData.model || formData.experiencePreferences.length === 0 || !formData.annualUsage || formData.excitementReasons.length === 0) {
      toast.error('Please complete all interest fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/interest`,
        {
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          phone: `${formData.countryCode}${formData.mobile}`,
          interest_type: formData.assetCategory,
          message: JSON.stringify(formData)
        },
        { withCredentials: true }
      );

      // Refresh user data
      const response = await axios.get(`${API_URL}/api/auth/session`, {
        withCredentials: true
      });
      setUser(response.data.user);
      
      setEditingMode(false);
      toast.success('Interest form updated successfully');
    } catch (error) {
      console.error('Interest form update error:', error);
      toast.error('Failed to update interest form');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full mx-4 shadow-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex justify-between items-center">
          <h3 className="text-2xl font-bold">Expression of Interest Form</h3>
          <div className="flex gap-3">
            {!editingMode ? (
              <>
                <Button 
                  onClick={() => setEditingMode(true)}
                  className="bg-gold text-black hover:bg-gold/90 flex items-center gap-2"
                >
                  <Edit2 size={16} />
                  Edit Form
                </Button>
                <Button 
                  onClick={onClose}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <X size={16} />
                  Close
                </Button>
              </>
            ) : (
              <>
                <Button 
                  onClick={handleSave}
                  disabled={loading}
                  className="bg-gold text-black hover:bg-gold/90 flex items-center gap-2"
                >
                  <Save size={16} />
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button 
                  onClick={() => {
                    setEditingMode(false);
                    setFormData({
                      ...user.interest_form_data,
                      email: user.interest_form_data.email || user.email
                    });
                  }}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <X size={16} />
                  Cancel
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Form Content */}
        <div className="p-6 space-y-8">
          {/* Section 1: Personal Information */}
          <div>
            <h4 className="text-xl font-bold mb-6 text-gold">Section 1: About You</h4>
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">First Name*</label>
                  {editingMode ? (
                    <Input
                      value={formData.firstName}
                      onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                    />
                  ) : (
                    <p className="p-2 bg-gray-50 rounded">{formData.firstName || 'N/A'}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Last Name*</label>
                  {editingMode ? (
                    <Input
                      value={formData.lastName}
                      onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                    />
                  ) : (
                    <p className="p-2 bg-gray-50 rounded">{formData.lastName || 'N/A'}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Email Address*</label>
                {editingMode ? (
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                  />
                ) : (
                  <p className="p-2 bg-gray-50 rounded">{formData.email || 'N/A'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Mobile Number*</label>
                {editingMode ? (
                  <div className="flex gap-2">
                    <select
                      value={formData.countryCode}
                      onChange={(e) => setFormData({...formData, countryCode: e.target.value})}
                      className="w-32 px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="+91">🇮🇳 +91</option>
                      <option value="+1">🇺🇸 +1</option>
                      <option value="+44">🇬🇧 +44</option>
                      <option value="+971">🇦🇪 +971</option>
                      <option value="+65">🇸🇬 +65</option>
                      <option value="+61">🇦🇺 +61</option>
                    </select>
                    <Input
                      type="tel"
                      value={formData.mobile}
                      onChange={(e) => setFormData({...formData, mobile: e.target.value})}
                      placeholder="Enter mobile number"
                      className="flex-1"
                    />
                  </div>
                ) : (
                  <p className="p-2 bg-gray-50 rounded">{formData.countryCode} {formData.mobile || 'N/A'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">State*</label>
                {editingMode ? (
                  <select
                    value={formData.state}
                    onChange={(e) => setFormData({...formData, state: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="">Select your state</option>
                    {indianStates.map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                ) : (
                  <p className="p-2 bg-gray-50 rounded">{formData.state || 'N/A'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">City*</label>
                {editingMode ? (
                  <Input
                    value={formData.city}
                    onChange={(e) => setFormData({...formData, city: e.target.value})}
                    placeholder="Enter your city"
                  />
                ) : (
                  <p className="p-2 bg-gray-50 rounded">{formData.city || 'N/A'}</p>
                )}
              </div>
            </div>
          </div>

          {/* Section 2: Interest Details */}
          <div>
            <h4 className="text-xl font-bold mb-6 text-gold">Section 2: Your Interests</h4>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Preferred Asset Category*</label>
                {editingMode ? (
                  <select
                    value={formData.assetCategory}
                    onChange={(e) => setFormData({...formData, assetCategory: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="Luxury Cars">Luxury Cars</option>
                    <option value="Vacation Homes">Vacation Homes (Coming Soon)</option>
                    <option value="Private Aircraft">Private Aircraft (Coming Soon)</option>
                  </select>
                ) : (
                  <p className="p-2 bg-gray-50 rounded">{formData.assetCategory || 'N/A'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Preferred Automobile Brand*</label>
                {editingMode ? (
                  <select
                    value={formData.brand}
                    onChange={(e) => setFormData({...formData, brand: e.target.value, model: ''})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="">Select a brand</option>
                    {Object.keys(luxuryBrands).map(brand => (
                      <option key={brand} value={brand}>{brand}</option>
                    ))}
                  </select>
                ) : (
                  <p className="p-2 bg-gray-50 rounded">{formData.brand || 'N/A'}</p>
                )}
              </div>

              {formData.brand && (
                <div>
                  <label className="block text-sm font-medium mb-2">Preferred Model*</label>
                  {editingMode ? (
                    <select
                      value={formData.model}
                      onChange={(e) => setFormData({...formData, model: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="">Select a model</option>
                      {luxuryBrands[formData.brand]?.map(model => (
                        <option key={model} value={model}>{model}</option>
                      ))}
                    </select>
                  ) : (
                    <p className="p-2 bg-gray-50 rounded">{formData.model || 'N/A'}</p>
                  )}
                </div>
              )}

              {formData.model === 'Other' && editingMode && (
                <div>
                  <label className="block text-sm font-medium mb-2">Please specify model</label>
                  <Input
                    value={formData.otherModel}
                    onChange={(e) => setFormData({...formData, otherModel: e.target.value})}
                    placeholder="Enter model name"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-2">How would you like to experience {formData.brand || 'the vehicle'}?* (Select all that apply)</label>
                {editingMode ? (
                  <div className="space-y-2">
                    {experienceOptions.map(option => (
                      <label key={option} className="flex items-center gap-3 p-3 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                        <input
                          type="checkbox"
                          checked={formData.experiencePreferences.includes(option)}
                          onChange={() => handleExperienceToggle(option)}
                          className="w-4 h-4 text-gold"
                        />
                        <span className="text-sm">{option}</span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {formData.experiencePreferences.map((pref, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <Check size={16} className="text-gold" />
                        <p className="text-sm">{pref}</p>
                      </div>
                    ))}
                    {formData.experiencePreferences.length === 0 && <p className="p-2 bg-gray-50 rounded">None selected</p>}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">How long do you anticipate using the asset annually?*</label>
                {editingMode ? (
                  <select
                    value={formData.annualUsage}
                    onChange={(e) => setFormData({...formData, annualUsage: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="">Select duration</option>
                    {usageOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                ) : (
                  <p className="p-2 bg-gray-50 rounded">{formData.annualUsage || 'N/A'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">What excites you most about The Haute Circle?* (Select all that apply)</label>
                {editingMode ? (
                  <div className="space-y-2">
                    {excitementOptions.map(option => (
                      <label key={option} className="flex items-start gap-3 p-3 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                        <input
                          type="checkbox"
                          checked={formData.excitementReasons.includes(option)}
                          onChange={() => handleExcitementToggle(option)}
                          className="w-4 h-4 text-gold mt-1"
                        />
                        <span className="text-sm">{option}</span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {formData.excitementReasons.map((reason, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <Check size={16} className="text-gold mt-1" />
                        <p className="text-sm">{reason}</p>
                      </div>
                    ))}
                    {formData.excitementReasons.length === 0 && <p className="p-2 bg-gray-50 rounded">None selected</p>}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InterestFormModal;
